import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  adminApi,
  type AdminThumbnailGeneration,
  type PaginatedResponse,
} from '../../lib/api';

export function ThumbnailsPage() {
  const [generations, setGenerations] = useState<PaginatedResponse<AdminThumbnailGeneration> | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [worldFilter, setWorldFilter] = useState('');
  const [lightbox, setLightbox] = useState<AdminThumbnailGeneration | null>(null);

  useEffect(() => {
    const fetchGenerations = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await adminApi.getThumbnailGenerations({
          page,
          status: statusFilter ? (statusFilter as 'success' | 'rejected' | 'failed') : undefined,
          worldId: worldFilter || undefined,
        });
        setGenerations(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load thumbnails');
      } finally {
        setIsLoading(false);
      }
    };

    fetchGenerations();
  }, [page, statusFilter, worldFilter]);

  const statusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-success/20 text-success';
      case 'rejected':
        return 'bg-amber-500/20 text-amber-400';
      case 'failed':
        return 'bg-ember/20 text-ember';
      default:
        return 'bg-stone/30 text-mist';
    }
  };

  const truncate = (value: string | null, length: number) => {
    if (!value) return '—';
    return value.length > length ? `${value.slice(0, length)}...` : value;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="font-display text-2xl text-parchment">Thumbnails</h1>
        <div className="flex flex-wrap gap-2">
          <input
            value={worldFilter}
            onChange={(e) => {
              setWorldFilter(e.target.value.trim());
              setPage(1);
            }}
            placeholder="Filter by world id"
            className="px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm w-64"
          />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm"
          >
            <option value="">All Status</option>
            <option value="success">Success</option>
            <option value="rejected">Rejected</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-ember/10 border border-ember/30 rounded-lg text-ember">
          {error}
        </div>
      )}

      {isLoading && (
        <div className="text-center py-12">
          <div className="text-mist animate-pulse">Loading thumbnails...</div>
        </div>
      )}

      {!isLoading && generations && (
        <>
          <div className="bg-night border border-stone/30 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-mist text-left border-b border-stone/30 bg-stone/20">
                  <th className="px-4 py-3 font-medium">Created</th>
                  <th className="px-4 py-3 font-medium">World</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Attempts</th>
                  <th className="px-4 py-3 font-medium">Checks</th>
                  <th className="px-4 py-3 font-medium">Prompt</th>
                  <th className="px-4 py-3 font-medium">Preview</th>
                </tr>
              </thead>
              <tbody>
                {generations.data.map((generation) => (
                  <tr
                    key={generation.id}
                    className="border-b border-stone/20 last:border-0 hover:bg-stone/10"
                  >
                    <td className="px-4 py-3 text-mist text-xs whitespace-nowrap">
                      {formatDate(generation.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/worlds/${generation.worldId}`}
                        className="text-rune hover:underline text-xs font-mono"
                      >
                        {generation.worldId}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${statusColor(
                          generation.status,
                        )}`}
                      >
                        {generation.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-mist text-xs">{generation.attempts}</td>
                    <td className="px-4 py-3 text-xs text-mist">
                      <div>Text: {generation.textDetected ? 'Yes' : 'No'}</div>
                      <div>Quality: {generation.qualityRejected ? 'Rejected' : 'OK'}</div>
                    </td>
                    <td className="px-4 py-3 text-parchment text-xs max-w-xs">
                      {truncate(generation.prompt, 120)}
                    </td>
                    <td className="px-4 py-3">
                      {generation.imageUrl ? (
                        <button
                          onClick={() => setLightbox(generation)}
                          className="w-14 h-14 rounded-md bg-void border border-stone/30 overflow-hidden"
                        >
                          <img
                            src={generation.imageUrl}
                            alt="Thumbnail preview"
                            className="w-full h-full object-contain"
                          />
                        </button>
                      ) : (
                        <span className="text-mist/50 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {generations.data.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-mist">
                      No thumbnail generations found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {generations.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-mist">
                {generations.pagination.total} total thumbnails
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm bg-stone/30 text-mist rounded-lg hover:bg-stone/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm text-mist">
                  Page {page} of {generations.pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === generations.pagination.totalPages}
                  className="px-3 py-1.5 text-sm bg-stone/30 text-mist rounded-lg hover:bg-stone/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-void/80 backdrop-blur-sm"
            onClick={() => setLightbox(null)}
          />
          <div className="relative max-h-[90vh] max-w-[90vw]">
            <button
              onClick={() => setLightbox(null)}
              className="absolute -top-4 -right-4 bg-void text-mist hover:text-parchment rounded-full border border-stone/40 w-8 h-8 flex items-center justify-center"
              aria-label="Close image"
            >
              ×
            </button>
            <div className="flex flex-col gap-3 max-h-[90vh] max-w-[90vw]">
              {lightbox.imageUrl ? (
                <img
                  src={lightbox.imageUrl}
                  alt="World thumbnail"
                  className="max-h-[70vh] max-w-[90vw] rounded-lg object-contain bg-parchment/95"
                />
              ) : null}
              <div className="bg-void/95 border border-stone/40 rounded-lg p-3 text-xs text-mist space-y-2">
                <div className="text-parchment text-sm">Prompt</div>
                <div>{lightbox.prompt ?? '—'}</div>
                {lightbox.qualityReasons ? (
                  <div>
                    <div className="text-parchment text-sm mt-2">Quality Reasons</div>
                    <div className="text-mist">
                      {Array.isArray(lightbox.qualityReasons)
                        ? lightbox.qualityReasons.join('; ')
                        : String(lightbox.qualityReasons)}
                    </div>
                  </div>
                ) : null}
                {lightbox.errorMessage ? (
                  <div>
                    <div className="text-parchment text-sm mt-2">Error</div>
                    <div className="text-ember">{lightbox.errorMessage}</div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
}
